import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import React from 'react';
import {
  BenefitsSliceDefault,
  BenefitsSliceWithCards,
} from '../../../prismicio-types';

enum SLICE_VARIATION {
  default = 'default',
  withCards = 'withCards',
}

/**
 * Props for `Benefits`.
 */
export type BenefitsProps = SliceComponentProps<Content.BenefitsSlice>;

/**
 * Component for "Benefits" Slices.
 */
const Benefits = ({ slice }: BenefitsProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withCards &&
        withCardsVariation(slice)}
    </section>
  );
};

const defaultVariation = (slice: BenefitsSliceDefault): JSX.Element => {
  const { title, description, image, items } = slice.primary;
  return (
    <div className="px-5 md:px-16 py-16 lg:py-28 max-w-screen-2xl mx-auto">
      <div className="flex flex-col lg:flex-row text-white gap-20 items-center">
        <div className="lg:w-3/4">
          <div className="mb-6">
            <PrismicRichText field={title} />
          </div>
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
          <div className="flex flex-col gap-y-4 mt-12">
            {items.map((item, index) => (
              <div key={`item-${index}`} className="flex items-start gap-x-4">
                <PrismicNextImage
                  field={item.item_icon}
                  className="w-8 h-8 mt-4"
                />

                <div className="flex flex-col gap-y-2">
                  <PrismicRichText field={item.item_title} />
                  <PrismicRichText field={item.item_description} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:w-1/4">
          <div className="hidden lg:block">
            <PrismicNextImage field={image} />
          </div>
          <div className="block lg:hidden">
            <PrismicNextImage field={image.mobile} />
          </div>
        </div>
      </div>
    </div>
  );
};

const withCardsVariation = (slice: BenefitsSliceWithCards): JSX.Element => {
  const { title, cards } = slice.primary;
  return (
    <div className="bg-primary-deep-green bg-opacity-85 backdrop-blur-[11px]">
      <div className="max-w-screen-2xl mx-auto px-5 md:px-16 py-20 flex flex-col gap-10">
        <div className="text-white max-w-[33rem] mx-auto text-center">
          <PrismicRichText field={title} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {cards.map((card, index) => (
            <div
              key={`card-${index}`}
              className="flex rounded-[20px] border-2 border-primary-shade-green"
            >
              <div className="w-1/5 flex justify-center items-center rounded-[20px] bg-transparent-dark-green-gradient">
                <PrismicNextImage field={card.icon} />
              </div>
              <div className="flex flex-col p-6 gap-2 w-4/5">
                <div className="text-primary-shade-green">
                  <PrismicRichText field={card.item_title} />
                </div>
                <PrismicRichText
                  field={card.description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-medium-normal text-white">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Benefits;
